import React from "react";
import { Building2, Users2, Target, ShieldCheck, MapPin, Hash } from "lucide-react";
import { PageHeader, CTABand } from "./Services";
import { BRAND } from "../lib/brand";

const SECTIONS = [
  {
    icon: Target,
    title: "What PlayerReach is",
    body: "PlayerReach is a performance marketing, customer acquisition, affiliate management, data and marketing technology company focused on iGaming, sports betting and entertainment brands. We help brands acquire new players, grow brand visibility and unlock measurable revenue through accountable campaign delivery and data-led decisions.",
  },
  {
    icon: Users2,
    title: "Who PlayerReach serves",
    body: "Our work is built around three audiences. Operators and brands looking for customer acquisition and growth. Partners, affiliates and traffic suppliers looking to work with a credible counterparty. And legal, compliance and commercial stakeholders evaluating policies, governance and business legitimacy.",
  },
  {
    icon: Building2,
    title: "How PlayerReach works",
    body: "We work from a documented operating model: discover the brand and market context, segment audiences and define compliant campaign routes, activate across approved channels and the partner network, then optimise using performance, suppression and partner feedback loops.",
  },
  {
    icon: ShieldCheck,
    title: "Why compliance matters",
    body: "Acquisition activity in iGaming and entertainment sits inside a regulated landscape. We treat compliance as a standing requirement, not a feature. Our policies on data, traffic, marketing communications and partner due diligence are live operating documents, applied in the day-to-day work.",
  },
];

export default function About() {
  React.useEffect(() => {
    document.title = "About PlayerReach";
  }, []);
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About"
        title="A serious operator partner for player acquisition and audience strategy."
        subtitle="PlayerReach.io is the trading brand of Numima FZC LLC — built around documented standards, partner accountability and a focused commercial offer."
      />

      <section className="section-pad">
        <div className="container-pr">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SECTIONS.map((s, i) => (
              <div key={s.title} className="card-flat p-8" data-testid={`about-section-${i}`}>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-slate-50 border border-slate-200 text-[#0052FF]">
                  <s.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display mt-5 text-xl lg:text-2xl font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company information */}
      <section className="pb-20 lg:pb-28">
        <div className="container-pr">
          <div className="relative overflow-hidden rounded-2xl bg-[#0B1120] text-white p-10 lg:p-14">
            <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <p className="overline text-cyan-300">Company information</p>
                <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold tracking-tight">Operating entity and registered details.</h2>
                <p className="mt-5 text-slate-300 leading-relaxed">
                  PlayerReach.io is a trading brand of {BRAND.legalEntity}. Our operating entity holds the contractual relationships, the data processing roles and the policy framework that supports the work delivered under the PlayerReach brand.
                </p>
              </div>
              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoTile icon={Building2} label="Operating entity" value={BRAND.legalEntity} />
                  <InfoTile icon={Hash} label="Formation Number" value={BRAND.formationNumber} />
                  <InfoTile icon={MapPin} label="Registered office" value={BRAND.address} wide />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}

function InfoTile({ icon: Icon, label, value, wide }) {
  return (
    <div className={`rounded-lg border border-white/10 bg-white/[0.04] p-5 ${wide ? "sm:col-span-2" : ""}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-cyan-300" />
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-mono-pr">{label}</p>
      </div>
      <p className="mt-2 text-white font-medium leading-snug">{value}</p>
    </div>
  );
}
